import { FC, useCallback, useState } from 'react'
import { useDomain } from 'context'
import gql from 'graphql-tag'
import Link from 'next/link'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import {
  CoverLetterDetailInfo_CoverLetterFragment,
  CoverLetterDetailInfo_CurrentUserFragment,
  useCreateSelfIntroductionMutation,
} from 'generated/graphql'
import { useCoverLetterStore } from '@app.feature/coverletter/coverletter.write/screen/rightSection/menus/CoverLetterSearch/store/coverLetter'
import { mobileBreakPoint } from 'app.styled/media'
import ShareModal from 'components/ShareModal'
import useErrorSnackbar from 'hooks/useErrorSnackbar'
import useKeywordHighlighter from 'hooks/useKeywordHighlighter'
import ShareIcon from 'svgIcons/Share'
import WriteCoverLetter from 'svgIcons/WriteCoverLetterIcon'
import arrayFilterJoin from 'utils/arrayFilterJoin'
import { getPassedAtText, getSpecInfo } from 'utils/coverLetter'
import { useSearchStateContext } from '../CoverLetterScreen/useSearchState'
import CoverLetterWriteTooltip from './CoverLetterWriteTooltip'
import NewWindowAndBackButton from './NewWindowAndBackButton'
import ScrapButtonWithPopper from './ScrapButtonWithPopper'

interface CoverLetterDetailInfoProps {
  isSimplePage?: boolean
  coverLetter?: CoverLetterDetailInfo_CoverLetterFragment
  currentUser?: CoverLetterDetailInfo_CurrentUserFragment
  listLink?: string
}

const CoverLetterDetailInfo: FC<CoverLetterDetailInfoProps> = ({
  isSimplePage = false,
  coverLetter,
  currentUser,
  listLink,
}) => {
  const router = useRouter()
  const { linkareer } = useDomain()
  const [openShareModal, setOpenShareModal] = useState<boolean>(false)

  const { enqueueApolloError } = useErrorSnackbar()
  const [createSelfIntroduction] = useCreateSelfIntroductionMutation({
    onError: enqueueApolloError,
  })

  const setCoverLetterId = useCoverLetterStore(
    (state) => state.setCoverLetterId,
  )

  const passedAt = getPassedAtText(coverLetter)
  const handleShareButtonClick = useCallback(async () => {
    const windoNavigator: any = window.navigator
    if (windoNavigator && windoNavigator.share) {
      try {
        await windoNavigator.share({
          title: quote,
          url: `https://${linkareer}${router.asPath}`,
        })
      } catch {
        /* empty */
      }
    } else {
      setOpenShareModal(true)
    }
  }, [])

  const handleSetCoverLetterId = async () => {
    const result = await createSelfIntroduction({
      variables: {
        input: {
          title: '새 자기소개서',
          qnas: [
            {
              question: '',
              answer: '',
              charMaxSize: 1000,
            },
          ],
        },
      },
    })
    router.push({
      pathname: '/self-introduction',
      query: {
        id: result.data?.createSelfIntroduction.selfIntroduction?.id,
      },
    })
    setCoverLetterId(`${router.query.id}`)
  }

  const { search } = useSearchStateContext()
  const highlightedOrgName = useKeywordHighlighter({
    text: coverLetter?.organizationName || '-',
    keyword: search.organizationName,
  })

  const highlightedRole = useKeywordHighlighter({
    text: coverLetter?.role || '-',
    keyword: search.role,
  })

  const basicInfos = arrayFilterJoin([
    highlightedOrgName,
    highlightedRole,
    passedAt,
  ])

  const quote = `${arrayFilterJoin([
    coverLetter?.organizationName,
    coverLetter?.role,
    passedAt,
  ])} 합격자소서 입니다`

  return (
    <StyledWrapper>
      <h2 className="header-info">합격 자소서</h2>

      {/* 기업명/직무/합격연도 */}
      <h1
        className="basic-info"
        dangerouslySetInnerHTML={{ __html: basicInfos }}
      />

      {/* 스펙 */}
      <h3 className="spec-info">{getSpecInfo(coverLetter)}</h3>

      <div className="button-wrapper">
        <div className="button-wrapper-inner">
          {/* 자소서 작성 버튼 */}
          <StyledMobileWrapper>
            <Link href="/self-introduction/manage">
              <button className="cover-letter-write">
                <WriteCoverLetter /> 자소서 작성
              </button>
            </Link>
          </StyledMobileWrapper>
          <StyledDesktopTooltip>
            <CoverLetterWriteTooltip>
              <button
                className="cover-letter-write"
                onClick={handleSetCoverLetterId}
              >
                <WriteCoverLetter /> 자소서 작성
              </button>
            </CoverLetterWriteTooltip>
          </StyledDesktopTooltip>

          {/* 스크랩 버튼 */}
          <ScrapButtonWithPopper
            coverLetter={coverLetter}
            currentUser={currentUser}
          />
          {/* 공유 버튼 */}
          <button
            className="cover-letter-share"
            onClick={handleShareButtonClick}
          >
            <ShareIcon />
            <StyledShareText>공유하기</StyledShareText>
          </button>
        </div>
        <div className="new-back-button-wrapper">
          <NewWindowAndBackButton
            backButtonLink={listLink || ''}
            isSimplePage={isSimplePage}
          />
        </div>
      </div>

      {/* 공유 모달 */}
      <ShareModal
        quote={quote}
        url={`https://${linkareer}${router.asPath}`}
        open={openShareModal}
        onModalClose={() => setOpenShareModal(false)}
      />
    </StyledWrapper>
  )
}

export default CoverLetterDetailInfo

const StyledWrapper = styled.div`
  flex-grow: 1;
  .header-info {
    font-size: 16px;
    font-weight: bold;
    color: #01a0ff;
  }

  .basic-info {
    font-size: 24px;
    font-weight: normal;
    letter-spacing: -1.2px;
    margin-bottom: 10px;
    & .highlight {
      font-weight: 600;
    }
    ${mobileBreakPoint} {
      letter-spacing: -0.96px;
    }
  }

  .spec-info {
    font-size: 16px;
    font-weight: normal;
    letter-spacing: -0.8px;
    margin-bottom: 30px;
    ${mobileBreakPoint} {
      letter-spacing: -0.64px;
    }
  }

  .button {
    min-height: 50px;
  }

  .margin-right {
    margin-right: 10px;
  }

  .button-wrapper {
    display: flex;
    height: 50px;
    margin-bottom: 30px;
    justify-content: space-between;
    ${mobileBreakPoint} {
      margin-bottom: 40px;
      justify-content: center;
    }

    .button-wrapper-inner {
      display: flex;
      align-items: center;
      gap: 10px;

      .cover-letter-write,
      .cover-letter-share {
        transition: all 0.2s;
        cursor: pointer;
        color: #666666;
        border: 1px solid #cccccc;
        background-color: #fff;
        gap: 10px;
        display: flex;
        min-width: 120px;
        height: 50px;
        align-items: center;
        justify-content: center;
        border-radius: 5px;
        font-size: 16px;
        box-sizing: border-box;
        font-weight: bold;
        line-height: 1.5;
        border-radius: 5px;
      }
      .cover-letter-write {
        min-width: 130px;
        color: #01a0ff;
        border: 1px solid #01a0ff;
        &:hover {
          background-color: #f2faff;
        }
      }

      ${mobileBreakPoint} {
        width: 100%;
        > .cover-letter-write {
          width: 100%;
        }
        > .cover-letter-share {
          ${mobileBreakPoint} {
            min-width: 0;
            padding: 0px 15px;
            width: 50px;
            flex-shrink: 0;
          }
        }
      }
    }
  }

  .new-back-button-wrapper {
    display: flex;
  }
`

const StyledMobileWrapper = styled.div`
  display: none;

  ${mobileBreakPoint} {
    display: block;
    width: 100%;

    > .cover-letter-write {
      width: 100%;
    }
  }
`

const StyledDesktopTooltip = styled.div`
  display: block;

  ${mobileBreakPoint} {
    display: none;
  }
`

const StyledShareText = styled.span`
  display: inline;

  ${mobileBreakPoint} {
    display: none;
  }
`

gql`
  fragment CoverLetterDetailInfo_currentUser on User {
    ...CoverLetterScrap_currentUser
  }
  fragment CoverLetterDetailInfo_coverLetter on CoverLetter {
    id
    role
    content
    organizationName
    viewerParagraphScraps {
      id
      startOffset
      endOffset
      paragraph
    }
    ...CoverLetterUtils_getSpecInfo_coverLetter
    ...CoverLetterUtils_getPassedAtText_coverLetter
  }
`
